import { ExecutionBindingError } from "@/cowork/execution-context"
import { CoworkApiError } from "@/cowork/api"
import type { ServerProtocol } from "./server-protocol"

export type SessionProtocol = (sessionID: string) => Promise<ServerProtocol>

export async function visibleSessionProtocol(resolve: SessionProtocol, sessionID: string) {
  try {
    return await resolve(sessionID)
  } catch (error) {
    if (error instanceof CoworkApiError && (error.status === 403 || error.status === 404)) return
    throw error
  }
}

export function createSessionProtocol(input: {
  scope?: () => string
  context: (sessionID: string) => Promise<{
    remote_opencode_session_id: string
    session_metadata: Record<string, unknown>
  }>
}): SessionProtocol {
  const pending = new Map<string, Promise<ServerProtocol>>()
  let scope = input.scope?.()
  return (sessionID) => {
    const activeScope = input.scope?.()
    if (scope !== activeScope) {
      pending.clear()
      scope = activeScope
    }
    const existing = pending.get(sessionID)
    if (existing) return existing
    const value = input.context(sessionID).then((context): ServerProtocol => {
      if (activeScope !== input.scope?.()) throw new ExecutionBindingError()
      const pin = context.session_metadata.execution_transport
      if (context.remote_opencode_session_id !== sessionID || !pin || typeof pin !== "object" ||
        !("contract" in pin) || pin.contract !== "andmera.session-transport.v1" ||
        !("runtime_session_id" in pin) || pin.runtime_session_id !== sessionID ||
        !("protocol" in pin) || (pin.protocol !== "v1" && pin.protocol !== "v2")) throw new ExecutionBindingError()
      return pin.protocol
    }).catch((error) => {
      if (pending.get(sessionID) === value) pending.delete(sessionID)
      throw error
    })
    pending.set(sessionID, value)
    return value
  }
}

// Identity/lifecycle events are shared by both APIs. Consume them through the
// server's primary stream only; execution events belong to their session pin.
export function executionEventSession(event: { type: string; properties?: unknown; data?: unknown }) {
  if (!/^(message\.|session\.(?!(created|updated|deleted)$)|permission\.|question\.|todo\.)/.test(event.type)) return
  const data = (event.properties ?? event.data) as Record<string, unknown> | undefined
  if (!data) return
  if (typeof data.sessionID === "string") return data.sessionID
  for (const key of ["info", "part", "input", "message"]) {
    const item = data[key]
    if (item && typeof item === "object" && "sessionID" in item && typeof item.sessionID === "string") return item.sessionID
  }
}
